import type { RecordingBlock, DayOfWeek, TimePeriod } from './types';
import {
  DEFAULT_RECORDING_START_TIME,
  DEFAULT_RECORDING_END_TIME,
  TIME_PERIOD_AM,
  TIME_PERIOD_PM,
  RECORDING_SCHEDULE_CONFIG_KEY,
} from './constants';

/**
 * Parse recording schedule from device state into UI-friendly RecordingBlock[]
 */
export function parseScheduleFromState(deviceState: any): RecordingBlock[] {
  const schedules = deviceState?.cameraSettings?.[RECORDING_SCHEDULE_CONFIG_KEY]?.schedules;
  if (!schedules || !Array.isArray(schedules) || schedules.length === 0) {
    return [];
  }

  const blocks: RecordingBlock[] = [];
  let blockIndex = 0;

  for (const schedule of schedules) {
    const timeWindows = schedule.timeWindows;
    if (!timeWindows || !Array.isArray(timeWindows)) continue;

    for (const tw of timeWindows) {
      blocks.push({
        id: `block-${blockIndex++}`,
        startTime: tw.startTime || DEFAULT_RECORDING_START_TIME,
        endTime: tw.endTime || DEFAULT_RECORDING_END_TIME,
        continuousRecording: tw.continuousRecording ?? false,
        clipEventRecording: tw.clipEventRecording ?? false,
        snapshot: tw.snapshot ?? false,
        daysOfWeek: tw.daysOfWeek || [],
      });
    }
  }

  return blocks;
}

/**
 * Build API request body from UI RecordingBlock[]
 */
export function buildScheduleRequest(blocks: RecordingBlock[], t: (key: string) => string) {
  const timeWindows = blocks.map((block) => ({
    daysOfWeek: block.daysOfWeek,
    startTime: block.startTime,
    endTime: block.endTime,
    clipEventRecording: block.clipEventRecording,
    snapshot: block.snapshot,
    continuousRecording: block.continuousRecording,
  }));

  return {
    [RECORDING_SCHEDULE_CONFIG_KEY]: {
      schedules: [
        {
          name: t('bulkEditSettings.recording.scheduleName'),
          enabled: true,
          priority: 1,
          timeWindows,
        },
      ],
    },
    channelId: null,
  };
}

/**
 * Build API request body to delete/disable the entire recording schedule.
 */
export function buildDeleteScheduleRequest(t: (key: string) => string) {
  return {
    [RECORDING_SCHEDULE_CONFIG_KEY]: {
      schedules: [
        {
          name: t('bulkEditSettings.recording.scheduleName'),
          enabled: false,
          priority: 1,
          timeWindows: [],
        },
      ],
    },
    channelId: null,
  };
}

/**
 * Convert 24h time string ("HH:MM") to 12h format ({ hour, minute, period })
 */
export function to12Hour(time24: string): { hour: number; minute: number; period: TimePeriod } {
  const [h, m] = time24.split(':').map(Number);
  const period: TimePeriod = h >= 12 ? TIME_PERIOD_PM : TIME_PERIOD_AM;
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { hour, minute: m, period };
}

/**
 * Convert 12h format to 24h time string ("HH:MM")
 */
export function to24Hour(hour: number, minute: number, period: TimePeriod): string {
  let h = hour;
  if (period === TIME_PERIOD_AM && h === 12) h = 0;
  if (period === TIME_PERIOD_PM && h !== 12) h += 12;
  return `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/**
 * Check if a time string represents a valid time and endTime > startTime
 */
export function isValidTimeRange(startTime: string, endTime: string): boolean {
  if (!startTime || !endTime) return false;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const startMinutes = sh * 60 + sm;
  const endMinutes = eh * 60 + em;
  return endMinutes > startMinutes;
}

/**
 * Generate a unique block ID
 */
export function generateBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get the day label abbreviation from DayOfWeekEnum value
 */
export function getDayLabel(day: DayOfWeek, t: (key: string) => string): string {
  return t(`bulkEditSettings.recording.days.short.${day}`) || day;
}

/**
 * Get the full day name from DayOfWeekEnum value
 */
export function getDayFullName(day: DayOfWeek, t: (key: string) => string): string {
  return t(`bulkEditSettings.recording.days.full.${day}`) || day;
}

/**
 * All days of the week in order
 */
export const ALL_DAYS: DayOfWeek[] = [
  'mon' as DayOfWeek,
  'tue' as DayOfWeek,
  'wed' as DayOfWeek,
  'thu' as DayOfWeek,
  'fri' as DayOfWeek,
  'sat' as DayOfWeek,
  'sun' as DayOfWeek,
];
